import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { auth, db } from "../services/firebase";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
  setPersistence,
  browserLocalPersistence,
} from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { isStaffRole } from "../config/permissions";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [initializing, setInitializing] = useState(true);
  const [error, setError] = useState(null);

  // Fetch user data from Firestore
  const fetchUserData = async (firebaseUser) => {
    if (!firebaseUser) {
      setUserData(null);
      return;
    }

    try {
      const userDoc = await getDoc(doc(db, "users", firebaseUser.uid));
      // No users/{uid} doc is not staff either — same rule the login path
      // already applies (it throws on a missing doc). Without this, a
      // persisted session for such an account stayed truthy with an
      // undefined role and landed inside the dashboard shell instead of
      // being signed out (spec 024 review).
      if (!userDoc.exists() || !isStaffRole(userDoc.data()?.role)) {
        await signOut(auth);
        setUserData(null);
        return;
      }
      setUserData(userDoc.data());
    } catch (err) {
      console.error("Error fetching user data:", err);
      await signOut(auth);
    }
  };

  useEffect(() => {
    // Persist auth across reloads
    setPersistence(auth, browserLocalPersistence).catch(() => {});

    const unsub = onAuthStateChanged(
      auth,
      async (firebaseUser) => {
        setUser(firebaseUser);
        await fetchUserData(firebaseUser);
        setInitializing(false);
      },
      (err) => {
        setError(err);
        setInitializing(false);
      }
    );

    return () => unsub();
  }, []);

  const login = async (email, password) => {
    setError(null);
    const cred = await signInWithEmailAndPassword(auth, email, password);
    const snap = await getDoc(doc(db, "users", cred.user.uid));
    if (!isStaffRole(snap.data()?.role)) {
      await signOut(auth);
      throw new Error("This account has no dashboard access.");
    }
  };

  const logout = async () => {
    await signOut(auth);
    setUserData(null);
  };

  const value = useMemo(() => {
    // Merge user data with Firebase user object
    const mergedUser = user && userData ? { ...user, ...userData } : user;
    return { user: mergedUser, initializing, error, login, logout };
  }, [user, userData, initializing, error]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
